import { useEffect, useState } from 'react'
import { ActivityIndicator, Platform } from 'react-native'

import NetInfo from '@react-native-community/netinfo'
import { publishEvent } from '@zet/analytics'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { BottomSheet, Button, Icon, colors } from '@zet/design-system'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import DocumentFilled from '@zet/design-system/svg/DocumentFilled'
import { SH1 } from '@zet/design-system/typography'
import { useGenerateSharableLink } from '@zet/feature-brands/api/brands/hooks'
import { useApplyLink } from '@zet/feature-brands/hooks/useApplyLink'
import { i18n } from '@zet/localization'
import { getName } from '@zet/utils/stringConversion'

import {
  useGetUserConsents,
  useSaveUserConsents,
} from '../../api/userConsent/hooks'
import { UserConsentContent } from './userConsentContent'

type UserConsentProps = {
  brandId: number
  customerId: number
  customerName: string
  leadSource: string
  customerPhoneNumber: number
  isBottomSheetVisible: boolean
  generateLinkParams?: any
  setIsBottomSheetVisible: (x: boolean) => void
}
const UserConsent = ({
  brandId,
  customerId,
  customerName,
  leadSource,
  generateLinkParams,
  customerPhoneNumber,
  isBottomSheetVisible,
  setIsBottomSheetVisible,
}: UserConsentProps) => {
  const [ipAddress, setIpAddress] = useState('')
  const handleBottomSheet = () => {
    setIsBottomSheetVisible(false)
  }
  const { data, isLoading, refetch } = useGetUserConsents(
    brandId,
    customerId,
    ipAddress,
    generateLinkParams,
    brandId,
    customerName,
    handleBottomSheet
  )
  const navigation = useNavigation()
  const { mutate, isLoading: saveConsentsLoading } = useSaveUserConsents()
  const {
    mutate: fetchShareLink,
    isSuccess,
    isLoading: loadingFetchShareLink,
    data: shareLinkData,
  } = useGenerateSharableLink()

  const openBottomSheet = () => {
    setIsBottomSheetVisible(true)
  }

  const closeBottomSheet = () => {
    setIsBottomSheetVisible(false)
  }

  const { handleApply } = useApplyLink(
    shareLinkData,
    brandId,
    customerName,
    customerId,
    customerPhoneNumber,
    leadSource,
    openBottomSheet,
    closeBottomSheet
  )

  useEffect(() => {
    publishEvent('screen_view', {
      screen_name: 'customer-consent',
      partner_id: brandId,
    })
  }, [])

  useEffect(() => {
    if (isSuccess && shareLinkData) {
      handleApply()
    }
  }, [isSuccess, shareLinkData])

  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    if (!isLoading && !data?.data?.kreditBeeConsent && Platform.OS === 'web') {
      fetchShareLink({
        partnerId: brandId,
        customerId: customerId,
        leadSource: leadSource,
      })
    }
  }, [data, isLoading])

  const getIpAddress = async () => {
    const state = await NetInfo.fetch()
    const ipAddress = state?.details?.ipAddress
    setIpAddress(ipAddress)
  }

  useEffect(() => {
    if (!isLoading && data?.data?.kreditBeeConsent)
      publishEvent('screen_view', {
        screen_name: 'customer-consent_loaded',
        partner_id: brandId,
      })
  }, [data, isLoading])

  const generateLink = () => {
    if (Platform.OS === 'web') {
      fetchShareLink({
        partnerId: brandId,
        customerId: customerId,
        leadSource: leadSource,
      })
    } else {
      navigation?.replace('ShareCardLink', {
        generateLinkParams: generateLinkParams,
        routeName: 'missingFields',
        brandId: brandId,
        customerName: getName(customerName),
        customerId: customerId,
      })
    }
    closeBottomSheet()
  }

  const onSubmit = () => {
    mutate(
      {
        consentIds: data?.data?.kreditBeeConsent?.map(
          (consent) => consent.consentId
        ),
        customerId: customerId,
        partnerId: brandId,
        ipAddress: ipAddress,
      },
      {
        onSuccess: () => {
          generateLink()
        },
      }
    )
  }

  return (
    <BottomSheet
      visible={isBottomSheetVisible}
      disableBottomSheet={handleBottomSheet}
      onDismiss={handleBottomSheet}
      sheetName="customer-consent"
    >
      <YStack className="mb-3" style={{ minHeight: 300 }}>
        <XStack className="border-grays-30 items-center justify-between border-b px-3 pb-1">
          <XStack className="items-center">
            <DocumentFilled />
            <SH1 className="ml-2">{i18n.t('customerDetails.consent')}</SH1>
          </XStack>
          <Pressable className="p-2" onPress={handleBottomSheet}>
            <Icon type="Close" />
          </Pressable>
        </XStack>
        {!isLoading && data?.data?.kreditBeeConsent?.length > 0 ? (
          <UserConsentContent consentData={data?.data} />
        ) : (
          <Stack
            className="h-[250] items-center justify-center"
            style={{ height: 200 }}
          >
            <ActivityIndicator color={colors.primary[100]} />
          </Stack>
        )}

        {!isLoading && data?.data?.kreditBeeConsent?.length > 0 && (
          <Stack className="mx-3">
            <Button
              eventName="click_action"
              eventPayload={{
                item_name: 'customer-consent_submit',
                partner_id: brandId,
              }}
              size="lg"
              fullWidth
              disabled={saveConsentsLoading}
              loading={loadingFetchShareLink}
              onPress={onSubmit}
            >
              Accept and Continue
            </Button>
          </Stack>
        )}
      </YStack>
    </BottomSheet>
  )
}

export default UserConsent
